import cn from 'classnames'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import {
  PARTNER_LINK_TRANSLATION_KEYS,
  usePartnerPlanUrl,
} from 'partner360/components/partnerNameWithLink'
import { NEW_VIEW_MODE_URL_PARAM_VALUE } from 'partner360/hooks/viewModeUrlParam'

import { useCockpitPartnerLinkParams } from '../../../components/cockpitHierarchyTable/useCockpitPartnerLinkParams'

import styles from './AttentionPartnerLink.module.scss'

interface IAttentionPartnerLinkProps {
  partnerId: string
  /** Named in the hover card, so the link says which partner it opens. */
  partnerName: string
  /** `icon` beside a row; `text` as a card's own call, where it is the named way out; `menuItem`
   *  inside the card's action menu, where it is a named line. */
  variant?: 'icon' | 'text' | 'menuItem'
  className?: string
}

/** The partner's own plan page on the fiscal year and offering the cockpit stands on, opened in
 *  the planning view this tile is part of, not in the old one the page would otherwise fall back
 *  to. */
export const useAttentionPartnerPlanUrl = (partnerId: string) => {
  const { fYear, offering } = useCockpitPartnerLinkParams()

  return usePartnerPlanUrl({
    partnerId,
    fYear,
    programType: offering,
    viewMode: NEW_VIEW_MODE_URL_PARAM_VALUE,
  })
}

/**
 * The way from a partner row of the manager cockpit's drill-down to that partner's own plan page,
 * on the fiscal year and offering the cockpit stands on.
 *
 * It is an icon beside the row rather than the row's own call: picking the row opens the status'
 * section, which is the answer the tile is for, and leaving the tile is the second thing the row
 * offers rather than the first. The address is the app's own `usePartnerPlanUrl`, so this reaches
 * the same page every other partner link does.
 */
export const AttentionPartnerLink = ({
  partnerId,
  partnerName,
  variant = 'icon',
  className,
}: IAttentionPartnerLinkProps) => {
  const t = useSafetyTranslation()
  const partnerUrl = useAttentionPartnerPlanUrl(partnerId)

  /* In the menu the line already says where it goes and for whom, so it carries no hover card:
     a tooltip over an opened menu would repeat the line it covers. */
  if (variant === 'menuItem') {
    return (
      <a
        className={cn(styles.menuItem, className)}
        href={partnerUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
      >
        <i className="kbase kbase-external-link" aria-hidden="true" />
        <span className={styles.menuItemLabel}>
          <Translation span text={PARTNER_LINK_TRANSLATION_KEYS.openPartnerPlan} />
          {` — ${partnerName}`}
        </span>
      </a>
    )
  }

  /* On a card the way out is the only call the card has, so it says where it goes in words: an
     icon there would be a second unnamed control beside a named one. */
  if (variant === 'text') {
    return (
      <a
        className={cn(styles.text, className)}
        href={partnerUrl}
        target="_blank"
        rel="noreferrer"
        /* The card under it opens the status' section; the link is the other way out, so the
           click stops here rather than doing both. */
        onClick={(event) => event.stopPropagation()}
      >
        <Translation span text={PARTNER_LINK_TRANSLATION_KEYS.openPartnerPlan} />
      </a>
    )
  }

  return (
    <Tooltip
      tipContent={() => (
        <>
          <div>{partnerName}</div>
          <Translation text={PARTNER_LINK_TRANSLATION_KEYS.openPartnerPlan} />
        </>
      )}
    >
      <a
        className={cn(styles.link, className)}
        href={partnerUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={t(PARTNER_LINK_TRANSLATION_KEYS.openPartnerPlan)}
        /* The row under it opens the status' section; the link is the other way out, so the click
           stops here rather than doing both. */
        onClick={(event) => event.stopPropagation()}
      >
        <i className="kbase kbase-external-link" aria-hidden="true" />
      </a>
    </Tooltip>
  )
}

AttentionPartnerLink.displayName = 'AttentionPartnerLink'
